import createRemoteSettingHook from '@/utils/user/createRemoteSettingHook';

/**
 * One entry per free preset. `id` holds the preset's **slug**, not a numeric
 * id — it is the field name the console's repeatable row exposes:
 *
 *   { "slugs": [{ "id": "rain-fashion-editorial" }] }
 */
interface FreePresetEntry {
  id: string;
}

interface FreePresetsSetting {
  slugs: FreePresetEntry[];
}

const REMOTE_SETTING_NAME = 'genai_studio_free_presets';

/**
 * Nothing is free until the setting says so — a missing, malformed or timed-out
 * fetch must never hand out free generations.
 */
const DEFAULT_SETTING: FreePresetsSetting = {
  slugs: [],
};

/**
 * Deliberately shallow — it checks the wrapper, not the rows. `parseSlugs`
 * drops the entries it can't read, so one mistyped row costs only its own
 * preset; validating each one here would fall back to the default and quietly
 * take *every* free preset offline.
 */
const isFreePresetsSetting = (
  settings: unknown
): settings is FreePresetsSetting =>
  typeof settings === 'object' &&
  settings !== null &&
  Array.isArray((settings as FreePresetsSetting).slugs);

const { useSetting, getSetting } = createRemoteSettingHook({
  name: REMOTE_SETTING_NAME,
  defaultValue: DEFAULT_SETTING,
  validate: isFreePresetsSetting,
  // Selects the array rather than a primitive, but the reference is stable —
  // the same `slugs` off the same cached settings object every call — so the
  // factory's `Object.is` bail-out still holds and only the default → resolved
  // transition re-renders subscribers.
  select: (settings) => settings.slugs,
});

/**
 * The slug out of one row, normalised. Trimmed and lower-cased on both sides of
 * the comparison, so a stray space or capital in the console still matches the
 * preset instead of silently charging for it.
 */
const toSlug = (entry?: unknown): string => {
  if (typeof entry === 'object' && entry !== null) {
    const { id } = entry as { id?: unknown };
    if (typeof id === 'string') return id.trim().toLowerCase();
  }

  return '';
};

let parsedSource: FreePresetEntry[] | undefined;
let parsedSlugs = new Set<string>();

/**
 * Flattens the rows into a lookup set, memoised on the array itself — the gate
 * is read on every render of every mounted feed slide, while the setting
 * resolves once per session.
 */
const parseSlugs = (entries: FreePresetEntry[]): Set<string> => {
  if (entries !== parsedSource) {
    parsedSource = entries;
    parsedSlugs = new Set(entries.map(toSlug).filter(Boolean));
  }

  return parsedSlugs;
};

const normalizeSlug = (slug?: string | null): string =>
  typeof slug === 'string' ? slug.trim().toLowerCase() : '';

/**
 * Whether a preset generates for free, for callers outside React (the submit
 * path and the credits gate, which need the answer at click time rather than
 * at render). Shares the module cache with `useIsFreePreset`, so after the
 * first resolve this settles immediately; before it, awaiting is the point —
 * answering "not free" early would charge credits for a free preset.
 */
export const isFreePreset = async (slug?: string | null): Promise<boolean> => {
  if (!slug) return false;

  const { slugs } = await getSetting();

  return parseSlugs(slugs).has(normalizeSlug(slug));
};

/**
 * The display gate: whether *this* preset is on the remotely-configured free
 * list (`genai_studio_free_presets`). Consumers hide the cost and skip their
 * `useProjectCredits` quote — the run goes to the free-quota endpoint, so there
 * is no price to show or to wait on.
 *
 * Per-slug on purpose: the feed mounts a bar per slide, and only the slides
 * whose own preset is free hide their chip.
 */
const useIsFreePreset = (slug?: string | null): boolean => {
  const slugs = useSetting();

  return Boolean(slug) && parseSlugs(slugs).has(normalizeSlug(slug));
};

export default useIsFreePreset;
